import React, { useState } from 'react';
import { CenterMissingPayment } from 'models';
import { Colors } from 'library';
import { Avatar, Card, Chip, Modal, PillButton, Row, Stack, Text, TextArea } from 'library/ui';
import { format12HourTime, formatShortWeekdayMonthDayYear } from 'shared/Dates';
import { client, consoleLogInDev, getTotalHours } from 'shared';

export function MissingPaymentApprovalModal({
    missingPayment,
    onClose,
    open,
}: {
    missingPayment: CenterMissingPayment;
    onClose: () => void;
    open: boolean;
}) {
    const [centerComments, setCenterComments] = useState(missingPayment.center_details || '');
    const [errorMessage, setErrorMessage] = useState('');

    function approveOrReject(action: 'approve' | 'reject') {
        setErrorMessage('');
        client(`api/center-missing-payment/${missingPayment.id}/`, {
            method: 'PATCH',
            body: {
                status: action === 'approve' ? 'CENTER_CONFIRMED' : 'CENTER_REJECTED',
                center_details: centerComments,
            },
        })
            .then(onClose)
            .catch((error) => {
                consoleLogInDev(error);
                setErrorMessage(
                    'There was an error processing this payment. Please try again or reach out to support in the chat or at team@join-tandem.com',
                );
            });
    }

    if (!open) return null;

    return (
        <Modal title="Missing Payment Report" onClose={onClose} width={560}>
            <Stack gap="md">
                <Card tone="muted">
                    <Stack gap="sm">
                        <Row justify="between" align="center">
                            <Row gap="sm" align="center">
                                <Avatar
                                    imageUrl={missingPayment.profile_image?.image}
                                    size={50}
                                    initials={missingPayment.user.first_name[0] ?? ''}
                                />
                                <Text weight="bold">
                                    {missingPayment.user.first_name} {missingPayment.user.last_name}
                                </Text>
                            </Row>
                            <Chip variant="filled" bgColor={Colors.warning} textColor={Colors.white}>
                                Requires Approval
                            </Chip>
                        </Row>
                        <Text size="sm">
                            <b>Center:</b> {missingPayment.business_location.name}
                        </Text>
                        <Text size="sm">
                            <b>Date:</b> {formatShortWeekdayMonthDayYear(missingPayment.start_date)}{' '}
                        </Text>
                        <Text size="sm">
                            <b>Hours Reported:</b>{' '}
                            {format12HourTime(missingPayment.start_date, missingPayment.timezone)} -{' '}
                            {format12HourTime(missingPayment.end_date, missingPayment.timezone, true)}
                        </Text>
                        <Text size="sm">
                            <b>Break:</b> {missingPayment.break_minutes} minutes
                        </Text>
                        <Text size="sm" weight="bold">
                            Total Hours: {getTotalHours(missingPayment).toFixed(2)}
                        </Text>
                        <Text size="sm">
                            <b>{missingPayment.user.first_name}&apos;s Comments:</b> {missingPayment.worker_details}
                        </Text>
                    </Stack>
                </Card>
                <Text size="sm">
                    <b>Comments:</b> {missingPayment.center_details}
                </Text>
                <TextArea
                    value={centerComments}
                    placeholder="Please provide details about your response. (Required if denying)"
                    onChange={setCenterComments}
                    fullWidth
                />
                <Text size="sm" align="right">
                    <b>Pay Rate: ${missingPayment.pay_rate}/hour</b>
                </Text>
                <Row justify="end" gap="sm">
                    <PillButton
                        variant="secondary"
                        disabled={centerComments.length < 15}
                        onClick={() => approveOrReject('reject')}
                    >
                        Deny
                    </PillButton>
                    <PillButton onClick={() => approveOrReject('approve')}>Approve and Pay</PillButton>
                </Row>
                {errorMessage && (
                    <Text size="sm" tone="error">
                        {errorMessage}
                    </Text>
                )}
            </Stack>
        </Modal>
    );
}
